import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { API } from "../../config";
import { AiOutlineDown } from "react-icons/ai";
import styles from "./GenerateKit.module.css";
import { CategorySelector } from "./CategorySelector";
import { KitPreview } from "./KitPreview";
import { ProductSplitAccordion } from "./ProductSplitAccordion";
import { useGenerateKitForm } from "./hooks/useGenerateKitForm";

export const GenerateKit = () => {
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [formSectionCollapsed, setFormSectionCollapsed] = useState(false);
  const categoryDropdownRef = useRef(null);

  const hasKits = Array.isArray(kits) && kits.length > 0;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(e.target)
      ) {
        setCategoryDropdownOpen(false);
      }
    };
    if (categoryDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [categoryDropdownOpen]);

  useEffect(() => {
    const getCategories = async () => {
      const data = await fetch(`${API}/Admin/category/get`);
      const json = await data.json();
      setCategories(json.allcategory || []);
    };
    getCategories();
  }, []);

  const {
    formData,
    productSplit,
    expandedCategories,
    updateForm,
    updateLowMax,
    updateMediumMax,
    updateHighMin,
    updateRangePercentage,
    isCategorySelected,
    getSelectedSubIds,
    toggleCategoryExpand,
    toggleCategory,
    toggleSubcategory,
    validateForm,
  } = useGenerateKitForm(errors, setErrors);


  const handleGenerate = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const quantityVal =
        formData.quantity != null && formData.quantity !== ""
          ? Number(formData.quantity)
          : undefined;
      const payload = {
        budget: Number(formData.budget),
        ...(quantityVal != null && !isNaN(quantityVal) && { quantity: quantityVal }),
        categories: formData.categories,
        productSplit,
        budgetRangePercentage: formData.rangePercentage,
      };
      const response = await axios.post(
        `${API}/Admin/carts/generate-kit`,
        payload,
      );
      const resultKits = response.data?.bundles ?? [];
      if(!resultKits || resultKits.length === 0) {
        toast.error("Could not generate kits");
        return;
      }
      setKits(Array.isArray(resultKits) ? resultKits : []);
      if (Array.isArray(resultKits) && resultKits.length) {
        setFormSectionCollapsed(true);
        toast.success("Kits generated successfully");
      }
    } catch (err) {
      console.error("Generate kit failed:", err);
      toast.error(err.response?.data?.message || "Failed to generate kits");
    } finally {
      setLoading(false);
    }
  };

  const handleReplace = (kitIndex, productId, replacement) => {
    setKits((prev) => {
      const next = [...prev];
      const kit = next[kitIndex];
      if (!kit?.products) return prev;
      const productIndex = kit.products.findIndex(
        (p) => p.productId === productId,
      );
      if (productIndex === -1) return prev;
      const currentProduct = kit.products[productIndex];
      const pool = [
        { ...currentProduct, replacements: [] },
        ...(currentProduct.replacements ?? []),
      ];
      const newReplacements = pool.filter(
        (p) => p.productId !== replacement.productId,
      );
      const newProduct = {
        ...replacement,
        quantity: currentProduct.quantity ?? 1,
        replacements: newReplacements,
      };
      const newProducts = [...kit.products];
      newProducts[productIndex] = newProduct;
      const totalCost = newProducts.reduce(
        (sum, p) => sum + (p.price ?? 0) * (p.quantity ?? 1),
        0,
      );
      next[kitIndex] = { ...kit, products: newProducts, totalCost };
      return next;
    });
  };

  const categoryDisplayText = useMemo(() => {
    if (!formData.categories.length) return null;
    return formData.categories
      .map((c) => {
        const cat = categories.find((x) => x._id === c.categoryId);
        return cat?.name || "";
      })
      .filter(Boolean)
      .join(", ");
  }, [formData.categories, categories]);

  const isFormCollapsed = hasKits && formSectionCollapsed;

  return (
    <div
      className={`${styles.container} ${
        hasKits ? styles.containerWithKits : ""
      }`}
    >
      <div className={styles.leftSection}>
        <button
          type="button"
          className={`${styles.formSectionHeader} ${
            !hasKits ? styles.formSectionHeaderStatic : ""
          } ${isFormCollapsed ? styles.formSectionCollapsedBar : ""}`}
          onClick={() => hasKits && setFormSectionCollapsed((c) => !c)}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            paddingLeft: 0,
          }}
        >
          <h2 className={styles.sectionTitle}>Kit Creation Questionnaire</h2>
          {hasKits && (
            <AiOutlineDown
              className={`${styles.formSectionToggle} ${
                formSectionCollapsed ? "" : styles.open
              }`}
            />
          )}
        </button>
        {(!hasKits || !formSectionCollapsed) && (
          <div className={styles.formContainer}>
            <p className={styles.sectionSubtitle}>
              Fill in the details to generate optimized kit options
            </p>

            <div className={styles.fieldWrapper}>
            <label className={styles.fieldLabel}>Budget</label>
            <div
              className={`${styles.inputWithPrefix} ${
                errors.budget ? styles.inputError : ""
              }`}
            >
              <span className={styles.prefix}>₹</span>
              <input
                type="number"
                min="0"
                max="100000"
                className={styles.input}
                placeholder="0"
                value={formData.budget || ""}
                onChange={(e) => updateForm("budget", e.target.value)}
              />
            </div>
            {errors.budget && (
              <span className={styles.errorMessage}>{errors.budget}</span>
            )}
          </div>

          <div className={styles.fieldWrapper}>
            <label className={styles.fieldLabel}>Quantity</label>
            <input
              type="number"
              min="1"
              max="50"
              className={`${styles.input} ${
                errors.quantity ? styles.inputError : ""
              }`}
              placeholder="Number products in kit"
              value={formData.quantity ?? ""}
              onChange={(e) => updateForm("quantity", e.target.value)}
            />
            {errors.quantity && (
              <span className={styles.errorMessage}>{errors.quantity}</span>
            )}
          </div>

          <div className={styles.fieldWrapper}>
            <label className={styles.fieldLabel}>Categories</label>
            <CategorySelector
              categories={categories}
              categoryDisplayText={categoryDisplayText}
              categoryDropdownOpen={categoryDropdownOpen}
              setCategoryDropdownOpen={setCategoryDropdownOpen}
              categoryDropdownRef={categoryDropdownRef}
              expandedCategories={expandedCategories}
              isCategorySelected={isCategorySelected}
              getSelectedSubIds={getSelectedSubIds}
              toggleCategoryExpand={toggleCategoryExpand}
              toggleCategory={toggleCategory}
              toggleSubcategory={toggleSubcategory}
            />
            {errors.categories && (
              <span className={styles.errorMessage}>{errors.categories}</span>
            )}
          </div>

          <ProductSplitAccordion
            accordionOpen={accordionOpen}
            setAccordionOpen={setAccordionOpen}
            formData={formData}
            productSplit={productSplit}
            errors={errors}
            updateLowMax={updateLowMax}
            updateMediumMax={updateMediumMax}
            updateHighMin={updateHighMin}
            updateRangePercentage={updateRangePercentage}
          />

            <button
              type="button"
              className={styles.generateButton}
              onClick={handleGenerate}
              disabled={loading}
            >
              {loading ? "Generating..." : "Generate Kits"}
            </button>
          </div>
        )}
      </div>

      <KitPreview
        kits={kits}
        budget={formData.budget}
        productSplit={productSplit}
        onReplace={handleReplace}
        onRegenerate={handleGenerate}
        loading={loading}
      />
    </div>
  );
};
